// services/account.service.js
import httpClient from './httpClient';
import TokenService from './token.service';

/**
 * Account Service
 * Maneja todas las operaciones relacionadas con Accounts (Personal y Organization)
 * Base URL configured via REACT_APP_AUTH_API_URL or REACT_APP_API_BASE_URL
 */

// Usar cliente HTTP centralizado
const authClient = httpClient;

const AccountService = {
  /**
   * Obtener cuenta personal del usuario
   * Opción 1: Usar default_account_id de localStorage (guardado en login)
   * Opción 2: Filtrar lista de cuentas por tipo PERSONAL
   */
  async getPersonalAccount() {
    try {
      // Opción 1: Intentar obtener desde default_account_id guardado en login
      const defaultAccountId = localStorage.getItem('default_account_id');
      
      if (defaultAccountId) {
        try {
          const response = await authClient.get(`/auth/accounts/${defaultAccountId}`);
          if (response.data?.body?.account?.type === 'PERSONAL') {
            return {
              success: true,
              data: response.data.body.account
            };
          }
        } catch (err) {
          console.warn('Could not fetch account by default_account_id, trying list method');
        }
      }

      // Opción 2: Listar todas las cuentas y filtrar por PERSONAL
      const listResponse = await authClient.get('/auth/accounts');
      const accounts = listResponse.data?.body?.accounts;
      
      // Validar que accounts sea un array
      if (!Array.isArray(accounts)) {
        return {
          success: false,
          error: ['Invalid accounts response format']
        };
      }
      
      // Buscar la cuenta personal (tipo PERSONAL con owner_id del usuario actual)
      const personalAccount = accounts.find(account => account.type === 'PERSONAL');
      
      if (personalAccount) {
        // Guardar el ID para futuras llamadas
        localStorage.setItem('default_account_id', personalAccount.id);
        
        return {
          success: true,
          data: personalAccount
        };
      }
      
      // Si no se encuentra ninguna cuenta personal
      return {
        success: false,
        error: ['No personal account found']
      };
      
    } catch (error) {
      console.error('Error fetching personal account:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to fetch personal account']
      };
    }
  },

  /**
   * Obtener cuenta de organización
   * GET /organizations/{organization_id}
   */
  async getOrganizationAccount(organizationId) {
    try {
      // Usar el endpoint correcto de organizations, no accounts
      const response = await authClient.get(`/auth/organizations/${organizationId}`);
      return {
        success: true,
        data: response.data.body.organization
      };
    } catch (error) {
      console.error('Error fetching organization account:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to fetch organization account']
      };
    }
  },

  /**
   * Obtener detalles de una cuenta por ID
   * GET /accounts/{account_id}
   */
  async getAccount(accountId) {
    try {
      const response = await authClient.get(`/auth/accounts/${accountId}`);
      return {
        success: true,
        data: response.data.body.account
      };
    } catch (error) {
      console.error('Error fetching account:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to fetch account']
      };
    }
  },

  /**
   * Actualizar cuenta
   * PUT /accounts/{account_id}
   */
  async updateAccount(accountId, updates) {
    try {
      const response = await authClient.put(`/auth/accounts/${accountId}`, updates);
      return {
        success: true,
        data: response.data.body.account
      };
    } catch (error) {
      console.error('Error updating account:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to update account']
      };
    }
  },

  /**
   * Listar todas las cuentas del usuario
   * GET /accounts?user_id=&organization_id=&type=&limit=20&offset=0
   * 
   * @param {Object} params - Parámetros de filtrado y paginación
   * @param {string} params.user_id - Filtrar por usuario (opcional)
   * @param {string} params.organization_id - Filtrar por organización (opcional)
   * @param {string} params.type - Filtrar por tipo PERSONAL/ORGANIZATION (opcional)
   * @param {number} params.limit - Registros por página (default: 20, max: 100)
   * @param {number} params.offset - Desplazamiento para paginación (default: 0)
   */
  async listAccounts({ user_id, organization_id, type, limit = 20, offset = 0 } = {}) {
    try {
      const params = {};
      if (user_id) params.user_id = user_id;
      if (organization_id) params.organization_id = organization_id;
      if (type) params.type = type;
      params.limit = limit;
      params.offset = offset;

      const response = await authClient.get('/auth/accounts', { params });
      
      return {
        success: true,
        data: response.data.body.accounts || [],
        totalCount: response.data.body.total_count || 0
      };
    } catch (error) {
      console.error('Error listing accounts:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to list accounts'],
        data: [],
        totalCount: 0
      };
    }
  },

  /**
   * Suspender cuenta
   * POST /accounts/{account_id}/suspend
   */
  async suspendAccount(accountId) {
    try {
      const response = await authClient.post(`/auth/accounts/${accountId}/suspend`);
      return {
        success: true,
        data: response.data.body
      };
    } catch (error) {
      console.error('Error suspending account:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to suspend account']
      };
    }
  },

  /**
   * Reactivar cuenta
   * POST /accounts/{account_id}/reactivate
   */
  async reactivateAccount(accountId) {
    try {
      const response = await authClient.post(`/auth/accounts/${accountId}/reactivate`);
      return {
        success: true,
        data: response.data.body
      };
    } catch (error) {
      console.error('Error reactivating account:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to reactivate account']
      };
    }
  },

  /**
   * Eliminar cuenta
   * DELETE /accounts/{account_id}
   */
  async deleteAccount(accountId) {
    try {
      const response = await authClient.delete(`/auth/accounts/${accountId}`);
      return {
        success: true,
        data: response.data.body
      };
    } catch (error) {
      console.error('Error deleting account:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to delete account']
      };
    }
  },

  /**
   * Obtener información del tier de una cuenta
   * GET /accounts/{account_id}/tier
   */
  async getAccountTier(accountId) {
    try {
      const response = await authClient.get(`/auth/accounts/${accountId}/tier`);
      
      // Intentar diferentes estructuras de respuesta
      const tierData = response.data.body?.tier || response.data.body || response.data;
      
      return {
        success: true,
        data: tierData
      };
    } catch (error) {
      console.error('Error fetching account tier:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to fetch account tier']
      };
    }
  },

  /**
   * Obtener información de un tier por ID
   * GET /tiers/{tier_id}
   */
  async getTier(tierId) {
    try {
      const response = await authClient.get(`/auth/tiers/${tierId}`);
      return {
        success: true,
        data: response.data.body.tier
      };
    } catch (error) {
      console.error('Error fetching tier:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to fetch tier']
      };
    }
  },

  /**
   * Listar todos los tiers disponibles
   * GET /tiers
   */
  async listTiers() {
    try {
      const response = await authClient.get('/auth/tiers');
      return {
        success: true,
        data: response.data.body.tiers || []
      };
    } catch (error) {
      console.error('Error listing tiers:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to list tiers'],
        data: []
      };
    }
  }
};

export default AccountService;
